import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Users, Building2, CreditCard } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { DashboardStats } from "./components/DashboardStats";
import { CompaniesTable } from "./components/CompaniesTable";

const SUPER_ADMIN_EMAILS = ['vagneriabot@gmail.com', 'oliveiravagnerof@gmail.com'];

const SuperAdminDashboard = () => {
  const navigate = useNavigate();

  // Check if user is super admin
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  useEffect(() => {
    if (user && !SUPER_ADMIN_EMAILS.includes(user.email || '')) {
      toast({
        variant: "destructive",
        title: "Acesso negado",
        description: "Você não tem permissão para acessar esta página.",
      });
      navigate('/');
    }
  }, [user, navigate]);

  const { data: companies = [], isLoading } = useQuery({
    queryKey: ['super-admin-companies'],
    queryFn: async () => {
      const { data } = await supabase
        .from('companies')
        .select(`
          *,
          profiles:company_id (count),
          properties:company_id (count)
        `);

      return data?.map(company => ({
        ...company,
        total_users: company.profiles.count,
        total_properties: company.properties.count
      })) || [];
    },
    enabled: !!user && SUPER_ADMIN_EMAILS.includes(user.email || ''),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const totalRevenue = companies.reduce((acc, company) => acc + (company.subscription_price || 0), 0);
  const totalUsers = companies.reduce((acc, company) => acc + company.total_users, 0);
  const totalProperties = companies.reduce((acc, company) => acc + company.total_properties, 0);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-gray-900">Painel Super Admin</h1>
          <p className="mt-2 text-gray-600">Gerenciamento de Imobiliárias</p>
        </div>
        <Button onClick={() => {}} className="flex items-center gap-2">
          <Download className="w-4 h-4" />
          Exportar Dados
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Imobiliárias</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{companies.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Usuários</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUsers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Imóveis</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalProperties}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita Mensal</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              R$ {totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Imobiliárias Cadastradas</CardTitle>
        </CardHeader>
        <CardContent>
          <CompaniesTable 
            companies={companies}
            onSuspendCompany={() => {}}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminDashboard;